import React, { useCallback, useEffect, useRef, useState } from 'react';

import TooltipWrapper from 'app/shared/components/common/TooltipWrapper';

import { replaceStringedBooleanValueWithBoolean } from 'utils/data';

import { initialTooltipState, SCROLL_DEBOUNCE_DELAY } from './constants';

const useTruncatedGeneral = ({ enterDelay, tooltipSx } = {}) => {
  const [tooltip, setTooltip] = useState(initialTooltipState);
  const isDisabled = useRef(false);
  const scrollTimerRef = useRef(null);

  const onResetTooltipState = useCallback(() => {
    setTooltip(initialTooltipState);
  }, []);

  const onScroll = useCallback(() => {
    isDisabled.current = true;
    setTooltip(initialTooltipState);

    if (scrollTimerRef.current) {
      clearTimeout(scrollTimerRef.current);
    }

    scrollTimerRef.current = setTimeout(() => {
      isDisabled.current = false;
    }, SCROLL_DEBOUNCE_DELAY);
  }, []);

  useEffect(
    () => () => {
      if (scrollTimerRef.current) {
        clearTimeout(scrollTimerRef.current);
      }
    },
    [],
  );

  const onMouseOver = useCallback(
    e => {
      if (isDisabled.current) {
        return;
      }

      const { target } = e;

      if (!replaceStringedBooleanValueWithBoolean(target.dataset?.truncated)) {
        onResetTooltipState();

        return;
      }

      const isTruncated =
        target.scrollWidth > target.clientWidth || target.scrollHeight > target.clientHeight;

      if (!isTruncated) {
        onResetTooltipState();

        return;
      }

      setTooltip({
        text:
          target.localName === 'input' && target !== document.activeElement
            ? target.value
            : target.innerText,
        visible: true,
        anchorEl: target,
      });
    },
    [onResetTooltipState],
  );

  const TruncatedProvider = () => {
    if (!tooltip.visible || !tooltip.anchorEl?.isConnected) {
      return null;
    }

    return (
      <TooltipWrapper
        disableInteractive
        anchorEl={tooltip.anchorEl}
        enterDelay={enterDelay}
        open={tooltip.visible}
        title={tooltip.text}
        {...(tooltipSx && { tooltipSx })}
      >
        <span className="tool__visually-hidden" />
      </TooltipWrapper>
    );
  };

  return [onMouseOver, onResetTooltipState, TruncatedProvider, onScroll];
};

export default useTruncatedGeneral;
